import { ClickAwayListener, Popper } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { DataProviderList } from "./DataProviderList";
import { deleteItemsFromList, deleteNamesFromCurrentText, getItemsToDelete } from "./Utils/UpdateSelectedItems";
import { isFunction } from "lodash";
export function DataProviderPopupList(props) {
    const { id, dataProvider, disabled, popUpChars, inputText, setInputText, selectedItems, setSelectedItems, onSelected, containerRef, inputElementRef, sx, offset } = props;
    const [prevInputText, setPrevInputText] = useState("");
    const [isSelectListOpen, setIsSelectListOpen] = useState(false);
    const [selectListSearchText, setSelectListSearchText] = useState("");
    const [anchorEl, setAnchorEl] = React.useState(null);
    const isPopupDisabled = disabled ?? false;
    const preventOverflow = props.preventOverflow ?? false;
    const initialPosition = props.initialPosition ?? "bottom-start";
    const cursorPosition = useRef(0);
    //to ensure popup renders inside container, container position must be relative as popup is absolute
    const container = containerRef && isFunction(containerRef) ? containerRef() : containerRef?.current;
    if (container) {
        container.style.position = "relative";
    }
    useEffect(() => {
        const inputBox = inputElementRef.current;
        if (inputBox && inputText) {
            if (inputText.includes(popUpChars)) {
                cursorPosition.current = inputBox.selectionStart ?? 0;
                //get position of the last popUpChars before the cursor position
                const previousPopUpCharsPosition = inputText.lastIndexOf(popUpChars, cursorPosition.current);
                //get position of the last space before the cursor position
                const previousSpacePosition = inputText.lastIndexOf(" ", cursorPosition.current - 1);
                if (cursorPosition.current >= previousPopUpCharsPosition + popUpChars.length) {
                    if (previousPopUpCharsPosition > previousSpacePosition) {
                        //we have no space between the last character typed and the popUpChars
                        setAnchorEl(inputBox);
                        setIsSelectListOpen(true);
                        inputBox.focus();
                        //get the text between the previous popUpChars and the cursor position
                        const searchText = previousPopUpCharsPosition >= 0 ? inputText.substring(previousPopUpCharsPosition + popUpChars.length, cursorPosition.current) : "";
                        setSelectListSearchText(searchText);
                    }
                    else {
                        // we have a space after the popUpChars, so no longer typing a name to lookup
                    }
                }
            }
            // Handle delete of a selected name
            const itemsToDelete = getItemsToDelete(selectedItems, prevInputText, inputText);
            const namesToDelete = itemsToDelete.map((item) => item.primaryText);
            if (itemsToDelete.length > 0) {
                const newText = deleteNamesFromCurrentText(prevInputText, inputText, namesToDelete);
                const newItems = deleteItemsFromList(selectedItems, itemsToDelete);
                setSelectedItems(newItems);
                setInputText(newText);
                setPrevInputText(newText);
            }
            else {
                setPrevInputText(inputText);
            }
        }
        else {
            setSelectedItems([]);
            setPrevInputText("");
            if (isSelectListOpen) {
                handleClose();
            }
        }
    }, [inputText, inputElementRef, popUpChars]);
    const handleOnSelected = async (e, value) => {
        const primaryText = value?.primaryText ?? "";
        const newInputText = inputText ? inputText.replace(`${popUpChars}${selectListSearchText}`, primaryText) : primaryText;
        setInputText(newInputText);
        setPrevInputText(newInputText);
        if (value && value.id) {
            if (!selectedItems.some((item) => item.id === value.id)) {
                setSelectedItems([...selectedItems, value]);
            }
            const entity = await dataProvider.getItemById(value.id);
            if (entity) {
                onSelected && onSelected(e, entity);
            }
        }
        handleClose();
        //set the cursor position to the end of the inserted primaryText and focus on the inputbox
        cursorPosition.current = cursorPosition.current + primaryText.length - 1;
        inputElementRef.current?.setSelectionRange(cursorPosition.current, cursorPosition.current);
        inputElementRef.current?.focus();
    };
    const clickAwayHandler = (event) => {
        if (isSelectListOpen) {
            handleClose();
        }
    };
    const handleClose = () => {
        setAnchorEl(null);
        setIsSelectListOpen(false);
    };
    return (React.createElement("div", { id: id, className: "aderant-popup-list", "data-testid": id }, React.createElement(ClickAwayListener, { onClickAway: clickAwayHandler },
        React.createElement(Popper, { id: id ? `${id}-dp-popup` : "", open: !isPopupDisabled && isSelectListOpen, disablePortal: container ? true : false, anchorEl: anchorEl, container: container, placement: initialPosition, modifiers: [
                {
                    name: "flip",
                    enabled: true,
                    options: {
                        altBoundary: true,
                        rootBoundary: "viewport",
                        padding: 8,
                        allowedAutoPlacements: ["top-start", "bottom-start"]
                    }
                },
                {
                    name: "preventOverflow",
                    enabled: preventOverflow,
                    options: {
                        mainAxis: true,
                        altAxis: false,
                        altBoundary: false,
                        tether: true,
                        boundary: "viewport",
                        padding: 8
                    }
                },
                {
                    name: "offset",
                    enabled: offset && offset.length === 2 ? true : false,
                    options: { offset: offset }
                }
            ], sx: sx?.popper, "aria-label": "Data Provider Popup" },
            React.createElement(DataProviderList, { id: id, dataProvider: dataProvider, onSelected: handleOnSelected, "aria-label": "Popup List", inputValue: selectListSearchText, sx: sx ? { list: sx?.list, listItem: sx?.listItem } : undefined })))));
}
